import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Avatar, crmToast, CrmToaster, SearchInput } from './crmUi'
import {
  CtLegacy, CtMonitorCard, CtSummary,
  ctByLead, ctLegacyConvert, ctLegacyDelete, ctLegacyList, ctLegacySave, ctList, ctListByLead, ctMarkPayment, ctMonitoring,
  getDeals,
} from './crmApi'

/**
 * CONTRACT MANAGER — THREE VIEWS (Lee, 14 Oct 2026 restructure; plan-split
 * same day): ONLY ENTERPRISE uses our contract system. Pro/Premium/Lite are
 * WeQuote-managed (signup, money and terms all on WeQuote; they send us the
 * facts) — read-only mirrors here. Cutoff: paid from Mon 19 Oct 2026 = New
 * T&Cs; everyone existing = Old T&Cs (rolling monthly, no fixed term). From
 * the new terms on, everyone is locked in for a fixed 12 months, monthly or
 * annual. CONVERT: any old-book company → new terms on any plan (Lite/Pro/
 * Premium via WeQuote mirror; Enterprise via our Contract Studio). On the
 * new book, Pro/Premium/Lite can be UPGRADED to Enterprise (our contract);
 * Enterprise on the new book has no convert — it is the end of the line.
 *  1. MONITORING (primary) — live money view over contracts sent from THIS
 *     system: Upcoming Payments · Upcoming Renewals · Recent Payments ·
 *     Payments Failed. Schedule is derived from countersigned contracts
 *     (commencement = countersign date); paid/failed are manual marks for
 *     now, shaped for Stripe later.
 *  2. OLD T&Cs — companies still on the old terms (Lee supplies the data;
 *     add/edit here meanwhile). Pro/Premium/Enterprise columns with MRR,
 *     sortable by duration/frequency, filterable by Snagg · Proc&Inv ·
 *     Integrations. Every card has CONVERT TO NEW CONTRACT. Countersigning
 *     a new contract auto-moves the company to the New T&Cs tab.
 *  3. NEW T&Cs — companies contracted (or being contracted) on the new
 *     system. Same columns/sorts. A company card opens its contract history:
 *     open any contract, RENEW one (same contract pre-filled) or NEW
 *     CONTRACT (fresh defaults).
 */

const SYM: Record<string, string> = { GBP: '£', USD: '$', EUR: '€' }
const money = (n: number, cur = 'GBP') => `${SYM[cur] || '£'}${Math.round(n).toLocaleString('en-GB')}`
const dmy = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—')

const PLANS: Array<{ key: 'lite' | 'pro' | 'premium' | 'enterprise'; label: string; color: string }> = [
  { key: 'lite', label: 'Lite', color: '#64748b' },
  { key: 'pro', label: 'Pro', color: '#0ea5e9' },
  { key: 'premium', label: 'Premium', color: '#8b5cf6' },
  { key: 'enterprise', label: 'Enterprise', color: '#f59e0b' },
]
const planKey = (p: string) => (PLANS.some((x) => x.key === p) ? p : 'pro')

const CT_BADGE: Record<string, { label: string; cls: string }> = {
  draft: { label: 'DRAFT', cls: 'bg-surface-tertiary text-txt-secondary border-border' },
  sent: { label: 'SENT', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  signed: { label: 'SIGNED — COUNTERSIGN', cls: 'bg-amber-50 text-amber-700 border-amber-300' },
  countersigned: { label: 'FULLY SIGNED', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  void: { label: 'VOID', cls: 'bg-red-50 text-red-600 border-red-200' },
}

const FlagChips: React.FC<{ snagg: boolean; proc: boolean; integ: boolean }> = ({ snagg, proc, integ }) => (
  <span className="inline-flex gap-1">
    {snagg && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-cyan-50 text-cyan-700 border border-cyan-200">SNAGG</span>}
    {proc && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-50 text-violet-700 border border-violet-200">PROC&amp;INV</span>}
    {integ && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-50 text-orange-700 border border-orange-200">INTEGR</span>}
  </span>
)

type SortKey = 'renewal' | 'duration' | 'frequency' | 'mrr'

export const CrmContracts: React.FC = () => {
  const navigate = useNavigate()
  const [view, setView] = useState<'monitor' | 'enquired' | 'old' | 'new'>('monitor')
  const [mon, setMon] = useState<Awaited<ReturnType<typeof ctMonitoring>> | null>(null)
  const [legacy, setLegacy] = useState<CtLegacy[]>([])
  const [contracts, setContracts] = useState<CtSummary[]>([])
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<SortKey>('renewal')
  const [flagFilter, setFlagFilter] = useState<{ snagg: boolean; proc: boolean; integ: boolean }>({ snagg: false, proc: false, integ: false })
  const [editLegacy, setEditLegacy] = useState<Partial<CtLegacy> | null>(null)
  const [convertRow, setConvertRow] = useState<CtLegacy | null>(null)
  const [companyOpen, setCompanyOpen] = useState<{ leadId: string; company: string } | null>(null)
  const [leadPicker, setLeadPicker] = useState<{ onPick: (leadId: string, name: string) => void; title: string } | null>(null)
  const [loading, setLoading] = useState(true)

  // Price Enquired leads (website form — Lee, 3 Nov 2026).
  const [enquired, setEnquired] = useState<any[]>([])
  const [enqOpen, setEnqOpen] = useState<string | null>(null)
  const [enqSubs, setEnqSubs] = useState<Record<string, any[]>>({})
  const reload = () => Promise.all([ctMonitoring(), ctLegacyList().catch(() => []), ctList().catch(() => []), getDeals({ stage: 'price_enquired', limit: 500 }).catch(() => ({ deals: [] }))])
    .then(([m, l, c, e]) => { setMon(m); setLegacy(l); setContracts(c); setEnquired((e as any).deals || []); setLoading(false) })
    .catch(() => { crmToast('Could not load the Contract Manager', 'error'); setLoading(false) })
  useEffect(() => { reload() }, [])

  /* New-T&Cs companies: one row per lead, led by its most relevant contract
     (countersigned first, else newest). */
  const companies = useMemo(() => {
    const byLead = new Map<string, CtSummary[]>()
    for (const c of contracts) {
      if (!byLead.has(c.leadId)) byLead.set(c.leadId, [])
      byLead.get(c.leadId)!.push(c)
    }
    return [...byLead.entries()].map(([leadId, list]) => {
      const sorted = [...list].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))
      const lead = sorted.find((c) => c.status === 'countersigned') || sorted[0]
      return { leadId, company: lead.company, lead, all: sorted }
    })
  }, [contracts])

  const oldRows = useMemo(() => legacy.filter((l) => l.tncs !== 'new' && !l.migratedAt), [legacy])
  const subs = useMemo(() => legacy.filter((l) => l.tncs === 'new' && !l.migratedAt), [legacy])

  /* New-T&Cs rows merge BOTH worlds: WeQuote-managed subscriptions (read-only
     mirrors) and companies on OUR Enterprise contracts. */
  type NewRow =
    | { kind: 'sub'; company: string; sub: CtLegacy }
    | { kind: 'contract'; company: string; c: { leadId: string; company: string; lead: CtSummary; all: CtSummary[] } }
  const newRows: NewRow[] = useMemo(() => ([
    ...subs.map((sub) => ({ kind: 'sub' as const, company: sub.company, sub })),
    ...companies.map((c) => ({ kind: 'contract' as const, company: c.company, c })),
  ]), [subs, companies])

  const applyFlagsAndSearch = <T extends { company: string }>(rows: T[], flagsOf: (r: T) => { snagg: boolean; proc: boolean; integ: boolean }) => {
    let out = rows
    if (q.trim()) { const n = q.trim().toLowerCase(); out = out.filter((r) => r.company.toLowerCase().includes(n)) }
    if (flagFilter.snagg) out = out.filter((r) => flagsOf(r).snagg)
    if (flagFilter.proc) out = out.filter((r) => flagsOf(r).proc)
    if (flagFilter.integ) out = out.filter((r) => flagsOf(r).integ)
    return out
  }

  const sortRows = <T,>(rows: T[], get: (r: T) => { renewal: string | null; duration: number; frequency: string; mrr: number }) =>
    [...rows].sort((a, b) => {
      const A = get(a); const B = get(b)
      if (sort === 'duration') return B.duration - A.duration
      if (sort === 'frequency') return A.frequency.localeCompare(B.frequency)
      if (sort === 'mrr') return B.mrr - A.mrr
      return String(A.renewal || '9999').localeCompare(String(B.renewal || '9999'))
    })

  const openStudio = (leadId: string, contractId?: string) => navigate(`/crm/contracts/${leadId}${contractId ? `?c=${contractId}` : ''}`)

  const newContract = (leadId: string) =>
    ctByLead(leadId, { fresh: true }).then(({ contract }) => openStudio(leadId, contract.id)).catch((e: any) => crmToast(e?.message || 'Could not create the contract', 'error'))
  const renewContract = (leadId: string, fromId: string) =>
    ctByLead(leadId, { renewFrom: fromId }).then(({ contract }) => { crmToast('Renewal created — values copied from the previous contract', 'success'); openStudio(leadId, contract.id) }).catch((e: any) => crmToast(e?.message || 'Could not create the renewal', 'error'))

  /** Enterprise conversion/upgrade = OUR contract via the Studio (needs a
   *  linked lead; countersigning auto-migrates the book row). */
  const enterpriseContract = (l: CtLegacy) => {
    if (l.leadId) { newContract(l.leadId); return }
    setLeadPicker({
      title: `Link "${l.company}" to a CRM lead first`,
      onPick: (leadId) => {
        ctLegacySave({ ...l, leadId } as any).then(() => { setLeadPicker(null); reload(); newContract(leadId) }).catch((e: any) => crmToast(e?.message || 'Could not link the lead', 'error'))
      },
    })
  }

  const mark = (c: CtMonitorCard, status: 'paid' | 'failed' | 'expected') =>
    ctMarkPayment(c.contractId, c.dueAt!, status).then(() => { crmToast(status === 'expected' ? 'Mark cleared' : `Marked ${status}`, 'success'); reload() }).catch((e: any) => crmToast(e?.message || 'Could not mark', 'error'))

  const TABS = [
    { key: 'monitor' as const, label: 'Monitoring', count: mon?.contracts ?? 0, hint: 'Payments & renewals on the new terms — ours and WeQuote-managed' },
    { key: 'enquired' as const, label: 'Price Enquired', count: enquired.length, hint: 'Website pricing-enquiry leads — create their contract from here' },
    { key: 'old' as const, label: 'Companies on Old T&Cs', count: oldRows.length, hint: 'Existing book — rolling monthly. Convert each to the new terms' },
    { key: 'new' as const, label: 'Companies on New T&Cs', count: newRows.length, hint: 'Paid from 19 Oct — fixed 12-month terms. Enterprise = our contracts; the rest WeQuote manages' },
  ]

  return (
    <div className="h-full flex flex-col bg-surface-secondary overflow-hidden">
      <CrmToaster />
      <div className="px-5 pt-4 pb-3 bg-surface border-b border-border">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-[18px] font-bold text-txt-primary">Contract Manager</h1>
          <span className="ml-auto flex items-center gap-2">
            {view !== 'monitor' && <SearchInput value={q} onChange={setQ} placeholder="Search companies…" />}
            {view === 'new' && (
              <>
                <button onClick={() => setEditLegacy({ plan: 'pro', frequency: 'monthly', tncs: 'new', durationMonths: 12 })}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold border border-border bg-surface text-txt-secondary hover:bg-surface-hover">+ Add WeQuote-managed</button>
                <button onClick={() => setLeadPicker({ title: 'New Enterprise contract — pick the company', onPick: (leadId) => { setLeadPicker(null); newContract(leadId) } })}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-brand-600 text-white hover:bg-brand-700">+ New Enterprise contract</button>
              </>
            )}
            {view === 'old' && (
              <button onClick={() => setEditLegacy({ plan: 'pro', frequency: 'monthly', tncs: 'old' })}
                className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-brand-600 text-white hover:bg-brand-700">+ Add company</button>
            )}
          </span>
        </div>
        {/* The three views — dead clear which does what. */}
        <div className="grid sm:grid-cols-3 gap-2 mt-3">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setView(t.key)}
              className={`rounded-xl border px-3 py-2 text-left transition-all ${view === t.key ? 'bg-brand-600 border-brand-600 text-white shadow-sm' : 'bg-surface border-border hover:border-brand-300'}`}>
              <span className="flex items-center gap-2">
                <span className={`text-[13px] font-bold ${view === t.key ? 'text-white' : 'text-txt-primary'}`}>{t.label}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${view === t.key ? 'bg-white/20 text-white' : 'bg-surface-tertiary text-txt-secondary'}`}>{t.count}</span>
              </span>
              <span className={`block text-[10px] mt-0.5 ${view === t.key ? 'text-white/80' : 'text-txt-tertiary'}`}>{t.hint}</span>
            </button>
          ))}
        </div>
        {(view === 'old' || view === 'new') && (
          <div className="flex items-center gap-2 flex-wrap mt-2.5">
            <span className="text-[10px] font-bold text-txt-tertiary uppercase">Sort</span>
            {([['renewal', 'Renewal date'], ['duration', 'Duration'], ['frequency', 'Payment frequency'], ['mrr', 'MRR']] as Array<[SortKey, string]>).map(([k, label]) => (
              <button key={k} onClick={() => setSort(k)} className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${sort === k ? 'bg-brand-600 text-white border-brand-600' : 'bg-surface text-txt-secondary border-border hover:bg-surface-hover'}`}>{label}</button>
            ))}
            <span className="text-[10px] font-bold text-txt-tertiary uppercase ml-2">Has</span>
            {([['snagg', 'Snagg users'], ['proc', 'Proc & Inventory'], ['integ', 'Integrations']] as Array<[keyof typeof flagFilter, string]>).map(([k, label]) => (
              <button key={k} onClick={() => setFlagFilter({ ...flagFilter, [k]: !flagFilter[k] })} className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${flagFilter[k] ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-surface text-txt-secondary border-border hover:bg-surface-hover'}`}>{label}</button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto p-4">
        {loading && <p className="text-[12px] text-txt-tertiary">Loading…</p>}

        {/* ═══ 1 · MONITORING ═══ */}
        {!loading && view === 'monitor' && mon && (
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3 items-start">
            {([
              { key: 'upcomingPayments', label: 'Upcoming Payments', color: '#0ea5e9', cards: mon.upcomingPayments, empty: 'No payments due in the next 45 days.' },
              { key: 'upcomingRenewals', label: 'Upcoming Renewals', color: '#8b5cf6', cards: mon.upcomingRenewals, empty: 'No renewals due in the next 60 days.' },
              { key: 'recentPayments', label: 'Recent Payments', color: '#10b981', cards: mon.recentPayments, empty: 'No payments fell due in the past 45 days.' },
              { key: 'failed', label: 'Payments Failed', color: '#ef4444', cards: mon.failed, empty: 'No failed payments. Long may it last.' },
            ] as const).map((col) => (
              <div key={col.key} className="rounded-xl bg-surface border border-border overflow-hidden min-w-0">
                <div className="px-3 py-2 border-b border-border flex items-center gap-2" style={{ borderTop: `3px solid ${col.color}` }}>
                  <span className="text-[12px] font-bold text-txt-primary">{col.label}</span>
                  <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-surface-tertiary text-txt-secondary">{col.cards.length}</span>
                </div>
                <div className="p-2 grid gap-2 max-h-[65vh] overflow-y-auto">
                  {col.cards.map((c, i) => (
                    <div key={`${c.contractId}-${c.dueAt || c.renewalAt}-${i}`} className="rounded-lg border border-border bg-surface-secondary p-2.5 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2 min-w-0">
                        {c.managedBy === 'wequote'
                          ? <span className="flex-1 min-w-0 text-[12px] font-bold text-txt-primary truncate">{c.company}</span>
                          : <button className="flex-1 min-w-0 text-left text-[12px] font-bold text-txt-primary truncate hover:text-brand-600" onClick={() => openStudio(c.leadId, c.contractId)}>{c.company}</button>}
                        <span className="ml-auto text-[12px] font-bold text-txt-primary shrink-0">{money(c.amount, c.currency)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-txt-tertiary">
                        <span className="capitalize">{c.plan}</span>
                        {c.managedBy === 'wequote' && <span className="px-1.5 rounded bg-sky-50 text-sky-700 font-bold border border-sky-200">WEQUOTE-MANAGED</span>}
                        <span>·</span><span>{c.frequency}</span><span>·</span>
                        <span className={c.overdue ? 'text-red-600 font-bold' : ''}>{col.key === 'upcomingRenewals' ? `renews ${dmy(c.renewalAt)}${c.overdue ? ' — OVERDUE' : ''}` : `due ${dmy(c.dueAt)}`}</span>
                        {c.status === 'paid' && <span className="px-1.5 rounded bg-emerald-50 text-emerald-700 font-bold border border-emerald-200">PAID</span>}
                        {c.status === 'failed' && <span className="px-1.5 rounded bg-red-50 text-red-600 font-bold border border-red-200">FAILED</span>}
                      </div>
                      <div className="flex gap-1.5 mt-1.5">
                        {col.key === 'upcomingRenewals'
                          ? (c.managedBy === 'wequote'
                            ? <span className="text-[10px] text-txt-tertiary">Renewal handled by WeQuote</span>
                            : <button onClick={() => renewContract(c.leadId, c.contractId)} className="px-2 py-0.5 rounded text-[10px] font-bold bg-brand-600 text-white hover:bg-brand-700">Renew now</button>)
                          : <>
                              {c.status !== 'paid' && <button onClick={() => mark(c, 'paid')} className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-600 text-white hover:bg-emerald-700">Mark paid</button>}
                              {c.status !== 'failed' && <button onClick={() => mark(c, 'failed')} className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-600 text-white hover:bg-red-700">Mark failed</button>}
                              {(c.status === 'paid' || c.status === 'failed') && <button onClick={() => mark(c, 'expected')} className="px-2 py-0.5 rounded text-[10px] font-bold bg-surface text-txt-secondary border border-border hover:bg-surface-hover">Clear</button>}
                            </>}
                      </div>
                    </div>
                  ))}
                  {col.cards.length === 0 && <p className="text-[11px] text-txt-tertiary px-1 py-2">{col.empty}</p>}
                </div>
              </div>
            ))}
            <p className="md:col-span-2 xl:col-span-4 text-[10px] text-txt-tertiary">Schedule is derived from each countersigned contract (commencement = countersign date, then every {`month/year`} by its payment frequency). Paid/Failed are manual marks for now — Stripe can feed them automatically later.</p>
          </div>
        )}

        {/* ═══ 2 · OLD T&Cs ═══ */}
        {!loading && view === 'enquired' && (
          <div className="max-w-[880px] space-y-2">
            {enquired.filter((d) => !q.trim() || `${d.name} ${d.contact || ''}`.toLowerCase().includes(q.toLowerCase())).map((d) => (
              <div key={d.id} className="rounded-xl border border-border bg-surface">
                <div className="flex items-center gap-3 px-3.5 py-2.5 flex-wrap">
                  <Avatar name={d.name} />
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-[13.5px] font-bold text-txt-primary">{d.name}</p>
                    <p className="text-[11.5px] text-txt-tertiary truncate">{[d.contact, d.contactRecord?.email, d.contactRecord?.phone || d.contactPhone].filter(Boolean).join(' · ') || '—'}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[9.5px] font-bold text-white" style={{ background: '#A855F7' }}>PRICE ENQUIRED</span>
                  <button onClick={() => {
                    const next = enqOpen === d.id ? null : d.id
                    setEnqOpen(next)
                    if (next && !enqSubs[d.id]) import('../../utils/api').then(({ apiGet }) => apiGet<{ submissions: any[] }>(`/crm/deals/${d.id}/form-submissions`).then((r) => setEnqSubs((p) => ({ ...p, [d.id]: r.submissions || [] }))).catch(() => {}))
                  }} className="text-[11px] font-bold text-brand-600 hover:underline">{enqOpen === d.id ? 'Hide answers' : 'Answers'}</button>
                  <button onClick={() => navigate(`/crm/lead/${d.id}`)} className="px-2.5 py-1 rounded-lg text-[11px] font-bold border border-border text-txt-secondary hover:bg-surface-hover">Open lead</button>
                  <button onClick={() => navigate(`/crm/contracts/${d.id}`)} className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-brand-600 text-white hover:bg-brand-700">Create contract</button>
                </div>
                {enqOpen === d.id && (
                  <div className="px-3.5 pb-3 space-y-1.5">
                    {(enqSubs[d.id] || []).map((f: any) => (
                      <div key={f.id} className="rounded-lg border border-border/70 bg-surface-secondary/40 px-2.5 py-2 text-[11.5px]">
                        <p className="font-bold text-txt-primary">{f.label} <span className="font-normal text-txt-tertiary">· {f.receivedAt ? new Date(f.receivedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span></p>
                        {Object.entries(f.answers || {}).map(([k, v]) => (
                          <div key={k} className="flex justify-between gap-3 py-0.5"><span className="text-txt-tertiary capitalize">{k.replace(/[_-]+/g, ' ')}</span><span className="text-txt-primary text-right whitespace-pre-wrap break-words max-w-[420px]">{String(v)}</span></div>
                        ))}
                        {!Object.keys(f.answers || {}).length && <p className="text-txt-tertiary">No extra answers — contact details only.</p>}
                      </div>
                    ))}
                    {!(enqSubs[d.id] || []).length && <p className="text-[11.5px] text-txt-tertiary">Loading…</p>}
                  </div>
                )}
              </div>
            ))}
            {!enquired.length && <p className="text-[12.5px] text-txt-tertiary py-8 text-center">No pricing enquiries yet — they arrive here from the website form.</p>}
          </div>
        )}
        {!loading && view === 'old' && (
          <PlanColumns
            rows={sortRows(applyFlagsAndSearch(oldRows, (l) => ({ snagg: l.hasSnagg, proc: l.hasProcurement, integ: l.hasIntegrations })), (l) => ({ renewal: l.nextRenewalAt, duration: l.durationMonths || 0, frequency: l.frequency || '', mrr: l.mrr || 0 }))}
            planOf={(l) => l.plan}
            mrrOf={(l) => l.mrr || 0}
            empty="No companies here yet — use + Add company, or upload the list to Claude to import."
            render={(l) => (
              <div key={l.id} className="rounded-lg border border-border bg-surface p-2.5 hover:border-brand-300 transition-all min-w-0 overflow-hidden">
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar name={l.company} size={24} />
                  <button className="flex-1 min-w-0 text-left text-[12px] font-bold text-txt-primary truncate hover:text-brand-600" title="Edit details" onClick={() => setEditLegacy(l)}>{l.company}</button>
                  <span className="ml-auto text-[12px] font-bold text-txt-primary shrink-0">{l.mrr != null ? `${money(l.mrr)}/mo` : '— MRR'}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap mt-1 text-[10px] text-txt-tertiary">
                  {l.tncsLabel && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-bold">{l.tncsLabel}</span>}
                  <span>{l.durationMonths ? `${l.durationMonths} mo` : 'Rolling'}</span><span>·</span>
                  <span>{l.frequency || 'monthly'}</span>
                  {l.nextRenewalAt && <><span>·</span><span>renews {dmy(l.nextRenewalAt)}</span></>}
                </div>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <FlagChips snagg={l.hasSnagg} proc={l.hasProcurement} integ={l.hasIntegrations} />
                  <button onClick={() => setConvertRow(l)} className="ml-auto px-2 py-1 rounded text-[10px] font-bold bg-brand-600 text-white hover:bg-brand-700">Convert to new T&amp;Cs →</button>
                </div>
              </div>
            )}
          />
        )}

        {/* ═══ 3 · NEW T&Cs — WeQuote-managed mirrors + OUR Enterprise contracts ═══ */}
        {!loading && view === 'new' && (
          <PlanColumns
            rows={sortRows(
              applyFlagsAndSearch(newRows, (r) => r.kind === 'sub'
                ? { snagg: r.sub.hasSnagg, proc: r.sub.hasProcurement, integ: r.sub.hasIntegrations }
                : { snagg: r.c.lead.hasSnagg, proc: r.c.lead.hasProcurement, integ: r.c.lead.hasIntegrations }),
              (r) => r.kind === 'sub'
                ? { renewal: r.sub.nextRenewalAt, duration: r.sub.durationMonths ?? 12, frequency: r.sub.frequency || 'monthly', mrr: r.sub.mrr || 0 }
                : { renewal: r.c.lead.termEndAt, duration: r.c.lead.termMonths, frequency: r.c.lead.frequency, mrr: r.c.lead.mrr })}
            planOf={(r) => planKey(r.kind === 'sub' ? r.sub.plan : r.c.lead.plan)}
            mrrOf={(r) => (r.kind === 'sub' ? r.sub.mrr || 0 : r.c.lead.status === 'countersigned' ? r.c.lead.mrr : 0)}
            empty="Nobody here yet — fills from Mon 19 Oct (new signups via WeQuote, Enterprise via our contracts)."
            render={(r) => r.kind === 'sub' ? (
              <div key={`s-${r.sub.id}`} className="rounded-lg border border-border bg-surface p-2.5 hover:border-brand-300 transition-all min-w-0 overflow-hidden">
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar name={r.sub.company} size={24} />
                  <button className="flex-1 min-w-0 text-left text-[12px] font-bold text-txt-primary truncate hover:text-brand-600" title="Edit details" onClick={() => setEditLegacy(r.sub)}>{r.sub.company}</button>
                  <span className="ml-auto text-[12px] font-bold text-txt-primary shrink-0">{r.sub.mrr != null ? `${money(r.sub.mrr)}/mo` : '— MRR'}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap mt-1 text-[10px] text-txt-tertiary">
                  <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 font-bold">WEQUOTE-MANAGED</span>
                  <span>{r.sub.durationMonths ?? 12} mo · {r.sub.frequency || 'monthly'}</span>
                  {r.sub.commencedAt && <span>· since {dmy(r.sub.commencedAt)}</span>}
                  {r.sub.nextRenewalAt && <span>· renews {dmy(r.sub.nextRenewalAt)}</span>}
                </div>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <FlagChips snagg={r.sub.hasSnagg} proc={r.sub.hasProcurement} integ={r.sub.hasIntegrations} />
                  {r.sub.plan !== 'enterprise' && (
                    <button onClick={() => enterpriseContract(r.sub)} className="ml-auto px-2 py-1 rounded text-[10px] font-bold bg-amber-600 text-white hover:bg-amber-700" title="Build their Enterprise contract in the Studio — countersigning moves them here as Enterprise">Upgrade to Enterprise →</button>
                  )}
                </div>
              </div>
            ) : (
              <button key={`c-${r.c.leadId}`} onClick={() => setCompanyOpen({ leadId: r.c.leadId, company: r.company })}
                className="rounded-lg border border-border bg-surface p-2.5 text-left hover:border-brand-300 hover:shadow-sm transition-all min-w-0 overflow-hidden">
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar name={r.company} size={24} />
                  <span className="flex-1 min-w-0 text-[12px] font-bold text-txt-primary truncate">{r.company}</span>
                  <span className="ml-auto text-[12px] font-bold text-txt-primary shrink-0">{money(r.c.lead.mrr, r.c.lead.currency)}/mo</span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap mt-1 text-[10px] text-txt-tertiary">
                  <span className={`px-1.5 py-0.5 rounded-full font-bold border ${(CT_BADGE[r.c.lead.status] || CT_BADGE.draft).cls}`}>{(CT_BADGE[r.c.lead.status] || CT_BADGE.draft).label}</span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-bold">OUR CONTRACT</span>
                  <span>{r.c.lead.termMonths} mo · {r.c.lead.frequency}</span>
                  {r.c.lead.termEndAt && <span>· renews {dmy(r.c.lead.termEndAt)}</span>}
                  {r.c.all.length > 1 && <span>· {r.c.all.length} contracts</span>}
                </div>
                <div className="mt-1.5"><FlagChips snagg={r.c.lead.hasSnagg} proc={r.c.lead.hasProcurement} integ={r.c.lead.hasIntegrations} /></div>
              </button>
            )}
          />
        )}
      </div>

      {editLegacy && <LegacyModal row={editLegacy} onClose={() => setEditLegacy(null)} onSaved={() => { setEditLegacy(null); reload() }} onPickLead={(cb) => setLeadPicker({ title: 'Link to a CRM lead', onPick: (id, name) => { setLeadPicker(null); cb(id, name) } })} />}
      {companyOpen && <CompanyContractsModal leadId={companyOpen.leadId} company={companyOpen.company} onClose={() => setCompanyOpen(null)} onOpen={openStudio} onNew={() => newContract(companyOpen.leadId)} onRenew={(fromId) => renewContract(companyOpen.leadId, fromId)} />}
      {convertRow && <ConvertModal row={convertRow} onClose={() => setConvertRow(null)}
        onWeQuotePlan={(plan) => {
          ctLegacyConvert(convertRow.id, { plan }).then(() => { setConvertRow(null); crmToast(`${convertRow.company} moved to the New T&Cs as ${plan} — WeQuote manages their signup, payment and terms`, 'success'); reload() }).catch((e: any) => crmToast(e?.message || 'Could not convert', 'error'))
        }}
        onEnterprise={() => { const row = convertRow; setConvertRow(null); enterpriseContract(row) }} />}
      {leadPicker && <LeadPickerModal title={leadPicker.title} onClose={() => setLeadPicker(null)} onPick={leadPicker.onPick} />}
    </div>
  )
}

/* ── Pro / Premium / Enterprise columns with MRR totals ── */
function PlanColumns<T>(p: { rows: T[]; planOf: (r: T) => string; mrrOf: (r: T) => number; render: (r: T) => React.ReactNode; empty: string }) {
  // Lite only earns a column when somebody is actually on it.
  const shown = PLANS.filter((plan) => plan.key !== 'lite' || p.rows.some((r) => p.planOf(r) === 'lite'))
  return (
    <div className={`grid gap-3 items-start ${shown.length === 4 ? 'md:grid-cols-2 xl:grid-cols-4' : 'md:grid-cols-3'}`}>
      {shown.map((plan) => {
        const rows = p.rows.filter((r) => p.planOf(r) === plan.key)
        const mrr = rows.reduce((a, r) => a + p.mrrOf(r), 0)
        return (
          <div key={plan.key} className="rounded-xl bg-surface border border-border overflow-hidden min-w-0">
            <div className="px-3 py-2 border-b border-border flex items-center gap-2" style={{ borderTop: `3px solid ${plan.color}` }}>
              <span className="text-[12px] font-bold text-txt-primary">{plan.label}</span>
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-surface-tertiary text-txt-secondary">{rows.length}</span>
              <span className="ml-auto text-[11px] font-bold text-txt-secondary whitespace-nowrap shrink-0">{money(mrr)}/mo MRR</span>
            </div>
            <div className="p-2 grid gap-2 max-h-[70vh] overflow-y-auto">
              {rows.map((r) => p.render(r))}
              {rows.length === 0 && <p className="text-[11px] text-txt-tertiary px-1 py-2">{p.empty}</p>}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/* ── Convert an Old-T&Cs company to the new terms: pick the destination plan.
      Lite/Pro/Premium → WeQuote-managed mirror; Enterprise → our contract. ── */
const ConvertModal: React.FC<{ row: CtLegacy; onClose: () => void; onWeQuotePlan: (plan: 'lite' | 'pro' | 'premium') => void; onEnterprise: () => void }> = ({ row, onClose, onWeQuotePlan, onEnterprise }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-surface rounded-xl border border-border shadow-xl w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
      <h3 className="text-[14px] font-bold text-txt-primary mb-1">Convert {row.company} to the new T&amp;Cs</h3>
      <p className="text-[11px] text-txt-tertiary mb-3">
        They can land on ANY plan — currently <b className="capitalize">{row.plan}</b>, {row.mrr != null ? `${'£'}${Math.round(row.mrr).toLocaleString('en-GB')}/mo, ` : ''}rolling monthly.
        The new terms lock them in for a fixed 12 months, paying monthly or annually.
      </p>
      <div className="grid gap-2">
        {([['lite', 'Lite'], ['pro', 'Pro'], ['premium', 'Premium']] as Array<['lite' | 'pro' | 'premium', string]>).map(([k, label]) => (
          <button key={k} onClick={() => onWeQuotePlan(k)} className="rounded-lg border border-border bg-surface-secondary p-2.5 text-left hover:border-sky-300 transition-all">
            <span className="flex items-center gap-2">
              <span className="text-[12px] font-bold text-txt-primary">{label}</span>
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-sky-50 text-sky-700 border border-sky-200">WEQUOTE-MANAGED</span>
            </span>
            <span className="block text-[10px] text-txt-tertiary mt-0.5">WeQuote handles their signup, payment and terms — we keep a read-only mirror on the New T&amp;Cs tab.</span>
          </button>
        ))}
        <button onClick={onEnterprise} className="rounded-lg border border-border bg-surface-secondary p-2.5 text-left hover:border-amber-300 transition-all">
          <span className="flex items-center gap-2">
            <span className="text-[12px] font-bold text-txt-primary">Enterprise</span>
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200">OUR CONTRACT</span>
          </span>
          <span className="block text-[10px] text-txt-tertiary mt-0.5">Opens the Contract Studio on a new contract for them. Countersigning it moves them to the New T&amp;Cs tab automatically.</span>
        </button>
      </div>
      <button className="mt-3 w-full px-3 py-1.5 rounded-lg text-[11px] font-bold border border-border text-txt-secondary hover:bg-surface-hover" onClick={onClose}>Cancel</button>
    </div>
  </div>
)

/* ── Book add/edit (old book + WeQuote-managed new book) ── */
const LegacyModal: React.FC<{ row: Partial<CtLegacy>; onClose: () => void; onSaved: () => void; onPickLead: (cb: (id: string, name: string) => void) => void }> = ({ row, onClose, onSaved, onPickLead }) => {
  const [f, setF] = useState<any>({ hasSnagg: false, hasProcurement: false, hasIntegrations: false, ...row })
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }))
  const inp = 'w-full px-2 py-1.5 rounded-lg border border-border bg-surface text-[12px] text-txt-primary'
  const lbl = 'block text-[10px] font-bold text-txt-tertiary uppercase mb-0.5'
  const save = () => {
    if (!String(f.company || '').trim()) { crmToast('Company name is required', 'error'); return }
    ctLegacySave({ ...f, mrr: f.mrr === '' || f.mrr == null ? null : Number(f.mrr), durationMonths: f.durationMonths === '' || f.durationMonths == null ? null : Number(f.durationMonths) })
      .then(onSaved).catch((e: any) => crmToast(e?.message || 'Could not save', 'error'))
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface rounded-xl border border-border shadow-xl w-full max-w-lg p-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-[14px] font-bold text-txt-primary mb-1">{(f.tncs === 'new' ? (row.id ? 'Edit WeQuote-managed company (New T&Cs)' : 'Add WeQuote-managed company (New T&Cs)') : (row.id ? 'Edit company on Old T&Cs' : 'Add company on Old T&Cs'))}</h3>
        <p className="text-[11px] text-txt-tertiary mb-3">{f.tncs === 'new'
          ? 'Read-only mirror of a subscription WeQuote manages (Lite/Pro/Premium). Fixed 12-month term, monthly or annual. This feeds the Monitoring view until the WeQuote data feed lands.'
          : 'The terms they follow TODAY — rolling monthly, no fixed term (leave duration blank for rolling). Use Convert on the card to move them to the new terms.'}</p>
        <div className="grid grid-cols-2 gap-2.5">
          <span className="col-span-2"><span className={lbl}>Company name</span><input className={inp} value={f.company || ''} onChange={(e) => set('company', e.target.value)} /></span>
          <span><span className={lbl}>Plan</span>
            <select className={inp} value={f.plan || 'pro'} onChange={(e) => set('plan', e.target.value)}>{PLANS.filter((pl) => f.tncs !== 'new' || pl.key !== 'enterprise').map((pl) => <option key={pl.key} value={pl.key}>{pl.label}</option>)}</select></span>
          <span><span className={lbl}>MRR (£/month)</span><input type="number" className={inp} value={f.mrr ?? ''} onChange={(e) => set('mrr', e.target.value)} /></span>
          <span><span className={lbl}>Duration (months)</span><input type="number" className={inp} placeholder={f.tncs === 'new' ? '12' : 'blank = rolling'} value={f.durationMonths ?? ''} onChange={(e) => set('durationMonths', e.target.value)} /></span>
          {f.tncs === 'new' && <span><span className={lbl}>Commenced (went paid)</span><input type="date" className={inp} value={f.commencedAt ? String(f.commencedAt).slice(0, 10) : ''} onChange={(e) => set('commencedAt', e.target.value || null)} /></span>}
          <span><span className={lbl}>Payment frequency</span>
            <select className={inp} value={f.frequency || ''} onChange={(e) => set('frequency', e.target.value || null)}><option value="">—</option><option value="monthly">Monthly</option><option value="annual">Annual</option></select></span>
          <span><span className={lbl}>Next renewal date</span><input type="date" className={inp} value={f.nextRenewalAt ? String(f.nextRenewalAt).slice(0, 10) : ''} onChange={(e) => set('nextRenewalAt', e.target.value || null)} /></span>
          {f.tncs !== 'new' && <span><span className={lbl}>T&amp;Cs they follow</span><input className={inp} placeholder="e.g. 2023 Standard Terms" value={f.tncsLabel || ''} onChange={(e) => set('tncsLabel', e.target.value)} /></span>}
          <span className="col-span-2 flex items-center gap-3 pt-1">
            {([['hasSnagg', 'Snagg users'], ['hasProcurement', 'Proc & Inventory'], ['hasIntegrations', 'Integrations']] as Array<[string, string]>).map(([k, label]) => (
              <label key={k} className="flex items-center gap-1.5 text-[11px] text-txt-secondary font-semibold">
                <input type="checkbox" checked={!!f[k]} onChange={(e) => set(k, e.target.checked)} /> {label}
              </label>
            ))}
          </span>
          <span className="col-span-2"><span className={lbl}>CRM lead</span>
            <button className="px-2 py-1.5 rounded-lg border border-border bg-surface-secondary text-[11px] text-txt-secondary hover:bg-surface-hover w-full text-left"
              onClick={() => onPickLead((id, name) => { set('leadId', id); set('dealName', name) })}>
              {f.leadId ? `Linked: ${f.dealName || f.leadId} (click to change)` : `Not linked — click to pick the lead${f.tncs === 'new' ? ' (needed to upgrade to Enterprise)' : ' (needed to convert)'}`}
            </button></span>
          <span className="col-span-2"><span className={lbl}>Notes</span><textarea className={inp} rows={2} value={f.notes || ''} onChange={(e) => set('notes', e.target.value)} /></span>
        </div>
        <div className="flex items-center gap-2 mt-3">
          {row.id && <button className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-red-600 border border-red-200 hover:bg-red-50" onClick={() => { if (window.confirm('Remove this company from the list?')) ctLegacyDelete(row.id!).then(onSaved).catch(() => crmToast('Could not delete', 'error')) }}>Delete</button>}
          <button className="ml-auto px-3 py-1.5 rounded-lg text-[11px] font-bold border border-border text-txt-secondary hover:bg-surface-hover" onClick={onClose}>Cancel</button>
          <button className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-brand-600 text-white hover:bg-brand-700" onClick={save}>Save</button>
        </div>
      </div>
    </div>
  )
}

/* ── Per-company contract history: open · renew · new ── */
const CompanyContractsModal: React.FC<{ leadId: string; company: string; onClose: () => void; onOpen: (leadId: string, contractId: string) => void; onNew: () => void; onRenew: (fromId: string) => void }> = ({ leadId, company, onClose, onOpen, onNew, onRenew }) => {
  const [list, setList] = useState<CtSummary[] | null>(null)
  useEffect(() => { ctListByLead(leadId).then(setList).catch(() => { crmToast('Could not load contracts', 'error'); setList([]) }) }, [leadId])
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface rounded-xl border border-border shadow-xl w-full max-w-2xl p-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1">
          <Avatar name={company} size={28} />
          <h3 className="text-[14px] font-bold text-txt-primary">{company} — contracts</h3>
          <button className="ml-auto px-3 py-1.5 rounded-lg text-[11px] font-bold bg-brand-600 text-white hover:bg-brand-700" onClick={onNew}>+ New contract (defaults)</button>
        </div>
        <p className="text-[11px] text-txt-tertiary mb-3">RENEW copies everything from that contract into a fresh one for you to go over and send. NEW starts from the standard defaults.</p>
        {!list && <p className="text-[11px] text-txt-tertiary">Loading…</p>}
        <div className="grid gap-2">
          {(list || []).map((c) => {
            const badge = CT_BADGE[c.status] || CT_BADGE.draft
            return (
              <div key={c.id} className="rounded-lg border border-border bg-surface-secondary p-2.5 flex items-center gap-2.5 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border shrink-0 ${badge.cls}`}>{badge.label}</span>
                <span className="text-[11px] text-txt-secondary">
                  <b className="text-txt-primary capitalize">{c.plan}</b> · {c.termMonths} mo · {c.frequency} · {money(c.mrr, c.currency)}/mo
                  {c.renewedFrom && <span className="text-txt-tertiary"> · renewal</span>}
                </span>
                <span className="text-[10px] text-txt-tertiary">created {dmy(c.createdAt)}{c.commencementAt ? ` · commenced ${dmy(c.commencementAt)}` : ''}{c.termEndAt ? ` · ends ${dmy(c.termEndAt)}` : ''}</span>
                <span className="ml-auto flex gap-1.5 shrink-0">
                  {(c.status === 'countersigned' || c.status === 'sent' || c.status === 'signed') && (
                    <button className="px-2 py-1 rounded text-[10px] font-bold bg-violet-600 text-white hover:bg-violet-700" onClick={() => onRenew(c.id)}>Renew</button>
                  )}
                  <button className="px-2 py-1 rounded text-[10px] font-bold border border-border text-txt-secondary hover:bg-surface-hover" onClick={() => onOpen(leadId, c.id)}>Open</button>
                </span>
              </div>
            )
          })}
          {list && list.length === 0 && <p className="text-[11px] text-txt-tertiary">No contracts yet — create the first with + New contract.</p>}
        </div>
      </div>
    </div>
  )
}

/* ── Lead picker (search all deals) ── */
const LeadPickerModal: React.FC<{ title: string; onClose: () => void; onPick: (leadId: string, name: string) => void }> = ({ title, onClose, onPick }) => {
  const [deals, setDeals] = useState<any[]>([])
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    // EVERY lead is linkable (Lee, 15 Oct 2026 - Dubai Audio): all stages
    // including trial expired / contacted / nurture / cancelled (that is
    // includeTerminal), PLUS soft-archived leads, which the default list
    // hides - merged in and labelled ARCHIVED. Linking or sending a contract
    // never changes the lead's stage; only the WeQuote feed moves stages.
    (async () => {
      const out: any[] = []
      for (let offset = 0; offset < 10000; offset += 1000) {
        const d = await getDeals({ limit: 1000, offset, includeTerminal: true, includeSuppliers: true })
        out.push(...(d.deals || []))
        if ((d.deals || []).length < 1000) break
      }
      const archived = await getDeals({ limit: 1000, archived: true, includeSuppliers: true }).catch(() => ({ deals: [] as any[], total: 0 }))
      for (const a of archived.deals || []) if (!out.some((d) => d.id === a.id)) out.push({ ...a, _archived: true })
      setDeals(out); setLoading(false)
    })().catch(() => { crmToast('Could not load leads', 'error'); setLoading(false) })
  }, [])
  const rows = useMemo(() => {
    const n = q.trim().toLowerCase()
    const list = n ? deals.filter((d) => String(d.name || '').toLowerCase().includes(n)) : deals
    return list.slice(0, 60)
  }, [deals, q])
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface rounded-xl border border-border shadow-xl w-full max-w-md p-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-[14px] font-bold text-txt-primary mb-2">{title}</h3>
        <SearchInput value={q} onChange={setQ} placeholder="Search leads…" />
        <div className="grid gap-1 mt-2 overflow-y-auto">
          {loading && <p className="text-[11px] text-txt-tertiary">Loading leads…</p>}
          {rows.map((d) => (
            <button key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-hover text-left" onClick={() => onPick(d.id, d.name)}>
              <Avatar name={d.name} size={22} />
              <span className="text-[12px] font-semibold text-txt-primary truncate">{d.name}</span>
              {d._archived && <span className="px-1 py-0.5 rounded text-[8px] font-bold bg-slate-100 text-slate-600 border border-slate-200 shrink-0">ARCHIVED</span>}
              {d.stage && <span className="text-[9px] text-txt-tertiary shrink-0">{String(d.stage).replace(/_/g, ' ')}</span>}
              <span className="ml-auto text-[10px] text-txt-tertiary truncate">{d.email || ''}</span>
            </button>
          ))}
          {!loading && rows.length === 0 && <p className="text-[11px] text-txt-tertiary">No leads match.</p>}
        </div>
      </div>
    </div>
  )
}
